import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";

import { MdmPolicyCommand } from "@devices/types";
import { DragDropContext, Droppable, DroppableProvided, DropResult } from "@hello-pangea/dnd";
import { TableBody as MuiTableBody, TableRow, Theme } from "@mui/material";
import { MinimalTable, MinimalTableHeader, MinimalTableHeaderCell } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { PaneElement } from "@sidePanel/shared/PaneElement";
import { ColumnDef, getCoreRowModel, Table, useReactTable } from "@tanstack/react-table";

import PolicyCommandItem from "./PolicyCommandItem";

const useStyles = makeStyles()((theme: Theme) => ({
    disabledTable: {
        backgroundColor: theme.palette.emteria.background.paperDeep,
    },
}));

interface PolicyCommandsTableProps {
    content: MdmPolicyCommand[];
    onUpdate?: (content: MdmPolicyCommand[]) => Promise<boolean>;
    readOnly?: boolean;
    loading?: boolean;
}

interface PolicyCommandsTableBodyProps {
    content: MdmPolicyCommand[];
    onUpdate?: (content: MdmPolicyCommand[]) => Promise<boolean>;
    readOnly: boolean;
    loading: boolean;
    table: Table<MdmPolicyCommand>;
    noDataMessage: string;
}

const columns: ColumnDef<MdmPolicyCommand>[] = [{ id: "drag" }, { id: "index" }, { id: "command" }, { id: "actions" }];

const PolicyCommandsTableBody = ({
    content,
    onUpdate,
    readOnly,
    loading,
    table,
    noDataMessage,
}: PolicyCommandsTableBodyProps) => {
    const { classes } = useStyles();

    const handleDragEnd = async (result: DropResult) => {
        // Dropped outside of the list
        if (!result.destination || !onUpdate) {
            return;
        }

        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        const isNoChange = sourceIndex === destinationIndex;
        if (isNoChange) {
            return;
        }

        const updatedContent = [...content];

        const [movedCommand] = updatedContent.splice(sourceIndex, 1);
        updatedContent.splice(destinationIndex, 0, movedCommand);

        await onUpdate(updatedContent);
    };

    if (readOnly) {
        return (
            <TableBody table={table} noDataMessage={noDataMessage}>
                {content.map((c, index) => (
                    <PolicyCommandItem index={index} content={content} policyCommand={c} key={index} readOnly />
                ))}
            </TableBody>
        );
    }

    if (!content.length) {
        return <TableBody table={table} noDataMessage={noDataMessage} children={null} />;
    }

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical" isDropDisabled={loading}>
                {(droppableProvided: DroppableProvided) => (
                    <MuiTableBody
                        className={loading ? classes.disabledTable : ""}
                        ref={droppableProvided.innerRef}
                        {...droppableProvided.droppableProps}
                    >
                        {content.map((c, index) => (
                            <PolicyCommandItem
                                index={index}
                                content={content}
                                policyCommand={c}
                                key={index}
                                onUpdate={onUpdate}
                                loading={loading}
                            />
                        ))}
                        {droppableProvided.placeholder}
                    </MuiTableBody>
                )}
            </Droppable>
        </DragDropContext>
    );
};

const PolicyCommandsTable = ({ content, onUpdate, readOnly = false, loading = false }: PolicyCommandsTableProps) => {
    const { t } = useTranslation(["sidepanels", "pages"]);

    const table = useReactTable<MdmPolicyCommand>({ data: content, columns, getCoreRowModel: getCoreRowModel() });

    return (
        <PaneElement.TableLayout contentDeps={[content.length]}>
            <MinimalTable stickyHeader>
                <MinimalTableHeader>
                    <TableRow>
                        <MinimalTableHeaderCell width={50} />
                        <MinimalTableHeaderCell align="center">
                            {t("sidepanels:groupPanel.table.header.index")}
                        </MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="left">{t("pages:policiesPage.table.header.rule")}</MinimalTableHeaderCell>
                        <MinimalTableHeaderCell align="right" />
                    </TableRow>
                </MinimalTableHeader>
                <PolicyCommandsTableBody
                    content={content}
                    onUpdate={onUpdate}
                    readOnly={readOnly}
                    loading={loading}
                    table={table}
                    noDataMessage={t("pages:policiesPage.section.rules.empty")}
                />
            </MinimalTable>
        </PaneElement.TableLayout>
    );
};

export default PolicyCommandsTable;
